import * as Mapper from './util/Mapper.js';
import * as VonqProductService from './service/VonqProductService.js';
import * as VonqContractService from './service/VonqContractService.js';
import * as VonqCampaignService from './service/VonqCampaignService.js';
import * as VonqGroupService from './service/VonqGroupService.js';

const API = {};
const DEFAULT_ALIAS_PATTERN = /^(.+?)\s-\s(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2}\s(?:AM|PM))$/;

/**
 * Returns a promise for the api request.
 * All api requests should go through this route, so that errors can be handled correctly.
 * @param {*} props
 * @returns Promise
 */
export async function execute(props) {
    const { action } = props;
    if (!action || !API.hasOwnProperty(action)) {
        console.log('Method not supported', action, { props });
        return;
    }

   return API[action](props);
}

/**
 * Returns the contract types from vonq.
 * @param {input, size} param0
 * @returns Object
 */
API.searchProducts = async ({
    input = '',
    size = 50,
    excludeRecommend = false,
    sortBy = '',
    offset = '',
    industryId = '',
    jobFunctionId = '',
    jobTitleId = '',
    locationId = '',
    pagination,
    userId,
    ...props
}) => {
    const filters = new URLSearchParams(
        `name=${input}&limit=${size}&excludeRecommended=${excludeRecommend}&sortBy=${sortBy}&offset=${offset}&industryId=${industryId}&jobFunctionId=${jobFunctionId}&jobTitleId=${jobTitleId}&locationId=${locationId}`,
    );
    const response = await VonqProductService.searchProducts({ filters, userId });
    const { results } = response;
    props['userId'] = userId;
    pagination && pagination.init(response, props);
    return Mapper.mapList(results, [
        {
            a: 'title',
            b: 'name',
        },
        {
            a: 'product_id',
            b: 'id',
        },
        {
            a: 'homepage',
            b: 'url',
        },
        {
            a: 'logo_url.0.url',
            b: 'logo_url',
        },
        {
            a: 'logo_square_url.0.url',
            b: 'logo_square_url',
        },
        {
            a: 'logo_square_url.0.size',
            b: 'logo_square_size',
            mutations: {
                compute: {
                    mutate: (value) => {
                        if (value) {
                            const parts = value.split('x');
                            return parts && parts.length === 2
                                ? {
                                      height: value.split('x')[1],
                                      width: value.split('x')[0],
                                  }
                                : value;
                        }
                        return value;
                    },
                },
            },
        },
        {
            a: 'logo_rectangle_url.0.url',
            b: 'logo_rectangle_url',
        },
        {
            a: 'logo_rectangle_url.0.size',
            b: 'logo_rectangle_size',
            mutations: {
                compute: {
                    mutate: (value) => {
                        if (value) {
                            const parts = value.split('x');
                            return parts && parts.length === 2
                                ? {
                                      height: value.split('x')[1],
                                      width: value.split('x')[0],
                                  }
                                : value;
                        }
                        return value;
                    },
                },
            },
        },
        {
            a: 'description',
            b: 'description',
        },
        {
            a: 'channel.id',
            b: 'channel_id',
        },
        {
            a: 'vonq_price',
            b: 'vonq_price',
            mutations: {
                write: {
                    mutate: (value, destObj) => {
                        if (value && value.length > 0 && value[0].amount !== undefined && value[0].amount !== null) {
                            destObj['free_board'] = String(value[0].amount) === '0';
                        }
                        return value;
                    },
                },
            },
        },
        {
            a: 'duration',
            b: 'duration',
        },
        {
            a: 'duration',
            b: 'duration',
        },
        {
            a: 'locations',
            b: 'locations',
            mutations: {
                compute: {
                    mutate: (value) => {
                        if (value) {
                            return value.map((location) => {
                                return {
                                    id: location.id,
                                    name: location.canonical_name,
                                };
                            });
                        }
                        return value;
                    },
                },
            },
        },
        {
            a: 'industries',
            b: 'industries',
            mutations: {
                compute: {
                    mutate: (value) => {
                        if (value) {
                            return value.map((industry) => {
                                return {
                                    id: industry.id,
                                    name: industry.name,
                                };
                            });
                        }
                        return value;
                    },
                },
            },
        },
        {
            a: 'time_to_process.period',
            b: 'time_to_process',
        },
        {
            a: 'time_to_setup.period',
            b: 'time_to_setup',
        },
    ]);
};

/**
 * Returns products from vonq, with given ids.
 * @param {productIds} param0
 * @returns Object
 */
API.getProducts = async ({ productIds, userId }) => {
    const { results } = await VonqProductService.getProducts({ productIds, userId });
    return Mapper.mapList(results, [
        {
            a: 'product_id',
            b: 'id',
        },
        {
            a: 'description',
            b: 'description',
        },
    ]);
};

/**
 * Returns only the products that support contracts.
 * Input is the product name to search.
 * @param {input} param0
 * @returns Object
 */
API.getProductsSupportingContracts = async ({ input, limit = 50, userId }) => {
    const filters = new URLSearchParams(`search=${input}&limit=${limit}`);
    const { results } = await VonqProductService.getProductsSupportingContracts({ filters, userId });

    return Mapper.mapList(results, [
        {
            a: 'name',
            b: 'name',
            filter(value) {
                return !value.toLowerCase().includes('vdab');
            },
        },
        {
            a: 'id',
            b: 'id',
        },
        {
            a: 'url',
            b: 'url',
        },
        {
            a: 'logo_square_url.0.url',
            b: 'logo_url',
        },
    ]);
};

/**
 * Returns only the product that support contracts, with the provided id.
 * productId is the product id to find.
 * @param {productId} param0
 * @returns Object
 */
API.getProductSupportingContracts = async ({ channelId, userId }) => {
    const result = await VonqProductService.getProductSupportingContracts({ channelId, userId });
    return Mapper.map(result, [
        {
            a: 'id',
            b: 'id',
        },
        {
            a: 'channel.id',
            b: 'channel_id',
        },
        {
            a: 'name',
            b: 'name',
        },
        {
            a: 'mc_enabled',
            b: 'mc_enabled',
        },
        {
            a: 'setup_instructions',
            b: 'setup_instructions',
        },
        {
            a: 'setup_instructions',
            b: 'setup_instructions',
        },
        {
            a: 'contract_credentials',
            b: 'contract_credentials',
        },
        {
            a: 'logo_square_url.0.url',
            b: 'logo_url',
        },
        {
            a: 'feed_url',
            b: 'feed_url',
        },
        {
            a: 'manual_setup_required',
            b: 'manual_setup_required',
        },
        {
            a: 'posting_requirements',
            b: 'posting_requirements',
        },
    ]);
};

/**
 * Creates the contract record in Vonq and returns the result.
 * Credentials : form data used for third-party authentication
 * Channel Id: Used by vonq to identify the type of channel we wish to post to. E.g Linkedin.
 * @param {credentials, channel_id } param0
 * @returns Object
 */
API.createContract = async ({ credentials, channelId, userId, followedInstructions, optional, group }) => {
    const body = {
        credentials,
        channelId,
        followedInstructions,
        optional,
        group
    };

    const result = await VonqContractService.createContract({ body, userId });
    return Mapper.map(result, [
        {
            a: 'contract_id',
            b: 'contract_id',
        },
        {
            a: 'channel_id',
            b: 'channel_id',
        },
        {
            a: 'product.product_id',
            b: 'product_id',
        },
    ]);
};

/**
 * Returns the contract information for a given user session.
 * Include Extra : To return further product information and map to contract.
 * @param {includeExtra} param0
 * @returns Object
 */
API.getContracts = async ({ includeExtra, userId }) => {
    const { results } = await VonqContractService.getContracts({ userId });
    const contracts = Mapper.mapList(results, [
        {
            a: 'contract_id',
            b: 'contract_id',
        },
        {
            a: 'channel_id',
            b: 'channel_id',
        },
        {
            a: 'product.product_id',
            b: 'product_id',
        },
        {
            a: 'channel.name',
            b: 'name',
        },
        {
            a: 'channel.logo_url.0.url',
            b: 'logo_url',
        },
        {
            a: 'channel.logo_square_url.0.url',
            b: 'logo_square_url',
        },
        {
            a: 'channel.logo_square_url.0.size',
            b: 'logo_square_size',
            mutations: {
                compute: {
                    mutate: (value) => {
                        if (value) {
                            const parts = value.split('x');
                            return parts && parts.length === 2
                                ? {
                                      height: value.split('x')[1],
                                      width: value.split('x')[0],
                                  }
                                : value;
                        }
                        return value;
                    },
                },
            },
        },
        {
            a: 'channel.logo_rectangle_url.0.url',
            b: 'logo_rectangle_url',
        },
        {
            a: 'channel.logo_rectangle_url.0.size',
            b: 'logo_rectangle_size',
            mutations: {
                compute: {
                    mutate: (value) => {
                        if (value) {
                            const parts = value.split('x');
                            return parts && parts.length === 2
                                ? {
                                      height: value.split('x')[1],
                                      width: value.split('x')[0],
                                  }
                                : value;
                        }
                        return value;
                    },
                },
            },
        },
        {
            a: 'expiry_date',
            b: 'expiryDate',
        },
        {
            a: 'purchase_price.amount',
            b: 'amount',
        },
        {
            a: 'purchase_price.currency',
            b: 'currency',
        },
        {
            a: 'credits',
            b: 'credits',
        },
        {
            a: 'group',
            b: 'group',
        },
        {
            a: 'posting_duration_days',
            b: 'postingDurationDays',
        },
        {
            a: 'alias',
            b: 'alias',
            mutations: {
                compute: {
                    mutate: (value) => {
                        if (!value) return ''
                        return value.match(DEFAULT_ALIAS_PATTERN) ? '' : value
                    },
                },
            },
        },
    ]);

    if (includeExtra && contracts && contracts.length > 0) {
        const productIds = [];

        for (let i = 0; i < contracts.length; i++) {
            let contract = contracts[i];
            productIds.push(contract.product_id);
        }

        const products = await execute({ action: 'getProducts', productIds, userId });
        for (let i = 0; i < products.length; i++) {
            const { id, description } = products[i];
            for (let n = 0; n < contracts.length; n++) {
                const { product_id: productId } = contracts[n];
                if (String(productId) === String(id)) {
                    contracts[n]['description'] = description;
                    break;
                }
            }
        }
    }

    return contracts;
};

API.getContractsByIds = async ({ contractIds, userId }) => {
    const { results } = await VonqContractService.getContractsByIds({ contractIds, userId });
    return Mapper.mapList(results, [
        {
            a: 'contract_id',
            b: 'contract_id',
        },
        {
            a: 'channel_id',
            b: 'channel_id',
        },
        {
            a: 'product.product_id',
            b: 'product_id',
        },
        {
            a: 'channel.name',
            b: 'name',
        },
        {
            a: 'channel.logo_url.0.url',
            b: 'logo_url',
        },
        {
            a: 'channel.logo_square_url.0.url',
            b: 'logo_square_url',
        },
        {
            a: 'channel.logo_square_url.0.size',
            b: 'logo_square_size',
            mutations: {
                compute: {
                    mutate: (value) => {
                        if (value) {
                            const parts = value.split('x');
                            return parts && parts.length === 2
                                ? {
                                      height: value.split('x')[1],
                                      width: value.split('x')[0],
                                  }
                                : value;
                        }
                        return value;
                    },
                },
            },
        },
        {
            a: 'channel.logo_rectangle_url.0.url',
            b: 'logo_rectangle_url',
        },
        {
            a: 'channel.logo_rectangle_url.0.size',
            b: 'logo_rectangle_size',
            mutations: {
                compute: {
                    mutate: (value) => {
                        if (value) {
                            const parts = value.split('x');
                            return parts && parts.length === 2
                                ? {
                                      height: value.split('x')[1],
                                      width: value.split('x')[0],
                                  }
                                : value;
                        }
                        return value;
                    },
                },
            },
        },
        {
            a: 'expiry_date',
            b: 'expiryDate',
            mutations: {
                default: {
                    value: 'Not Specified',
                    replaceEmpty: true,
                },
            },
        },
        {
            a: 'purchase_price.amount',
            b: 'amount',
            mutations: {
                default: {
                    value: 'Not Specified',
                    replaceEmpty: true,
                },
            },
        },
        {
            a: 'purchase_price.currency',
            b: 'currency',
        },
        {
            a: 'credits',
            b: 'credits',
            mutations: {
                default: {
                    value: 'Not Specified',
                    replaceEmpty: true,
                },
            },
        },
        {
            a: 'group',
            b: 'group',
            mutations: {
                default: {
                    value: 'Not Specified',
                    replaceEmpty: true,
                },
            },
        },
        {
            a: 'posting_duration_days',
            b: 'postingDurationDays',
        },
        {
            a: 'alias',
            b: 'alias',
            mutations: {
                compute: {
                    mutate: (value) => {
                        if (!value) return ''
                        return value.match(DEFAULT_ALIAS_PATTERN) ? '' : value
                    },
                },
            },
        },
        {
            a: 'posting_requirements',
            b: 'postingRequirements',
        },
    ]);
};

API.deleteContract = async ({ includeExtra, userId, contractId }) => {
    const { results } = await VonqContractService.deleteContract({ userId, contractId });
    return results;
};

API.getTaxonomyIndustries = async ({ userId }) => {
    const results = await VonqProductService.getTaxonomyIndustries({ userId });
    return Mapper.mapList(results, [
        {
            a: 'name',
            b: 'label',
        },
        {
            a: 'id',
            b: 'value',
        },
    ]);
};

API.getTaxonomyJobFunctions = async ({ userId }) => {
    const results = await VonqProductService.getTaxonomyJobFunctions({ userId });

    const flatteredChildResults = [];

    while (results.length > 0) {
        const result = results.shift();
        if (result) {
            flatteredChildResults.push(result);
        }
        if (result.children && result.children.length > 0) {
            for (let i = 0; i < result.children.length; i++) {
                const childRecord = result.children[i];
                if (childRecord) {
                    if (result.name && childRecord.name) {
                        childRecord.name = `${result.name} - ${childRecord.name}`;
                    }
                    childRecord && results.unshift(childRecord);
                }
            }
        }
    }

    return Mapper.mapList(flatteredChildResults, [
        {
            a: 'name',
            b: 'label',
        },
        {
            a: 'id',
            b: 'value',
        },
    ]);
};

API.getTaxonomyJobtitles = async ({ userId, limit = 50, offset = '', text = '' }) => {
    const filters = new URLSearchParams(`limit=${limit}&offset=${offset}&text=${text}`);
    const { results } = await VonqProductService.getTaxonomyJobtitles({ userId, filters });
    return Mapper.mapList(results, [
        {
            a: 'id',
            b: 'value',
        },
        {
            a: 'name',
            b: 'label',
        },
        {
            a: 'job_function',
            b: 'jobFunction',
        },
    ]);
};

API.getTaxonomyLocations = async ({ userId, text = '' }) => {
    const filters = new URLSearchParams(`text=${text}`);
    const results = await VonqProductService.getTaxonomyLocations({ userId, filters });
    return Mapper.mapList(results, [
        {
            a: 'id',
            b: 'value',
        },
        {
            a: 'fully_qualified_place_name',
            b: 'label',
        },
    ]);
};

API.getTaxonomyEducationLevels = async ({ userId }) => {
    const results = await VonqProductService.getTaxonomyEducationLevels({ userId });
    return Mapper.mapList(results, [
        {
            a: 'id',
            b: 'value',
        },
        {
            a: 'name',
            b: 'label',
        },
    ]);
};

API.getTaxonomySeniority = async ({ userId }) => {
    const results = await VonqProductService.getTaxonomySeniority({ userId });
    return Mapper.mapList(results, [
        {
            a: 'id',
            b: 'value',
        },
        {
            a: 'name',
            b: 'label',
        },
    ]);
};

API.getProductTypes = async ({ userId }) => {
    const results = await VonqProductService.getProductTypes({ userId });
    return Mapper.mapList(results, [
        {
            a: 'key',
            b: 'value',
        },
        {
            a: 'name',
            b: 'label',
        },
    ]);
};

API.saveCampaign = async ({ userId, step, request, campaignId }) => {
    return await VonqCampaignService.saveCampaign({ userId, step, request, campaignId });
};

API.cancelCampaign = async ({ userId, campaignId }) => {
    return await VonqCampaignService.cancelCampaign({ userId, campaignId });
};

API.cancelCampaignProduct = async ({ userId, campaignId, campaignItemId }) => {
    return await VonqCampaignService.cancelCampaignProduct({ userId, campaignId, campaignItemId });
};

API.purchaseCampaign = async ({ userId, campaignId }) => {
    return await VonqCampaignService.purchaseCampaign({ userId, campaignId });
};

API.contractAutocompleteValues = async ({ userId, contractId, params }) => {
    return await VonqContractService.contractAutocompleteValues({ userId, contractId, params });
};

API.listCampaigns = async ({ userId, request, filter }) => {
    const filterURI = new URLSearchParams(filter).toString();
    return await VonqCampaignService.listCampaign({
        userId,
        request,
        filterURI
    });
};

API.savePostingRequirementsData = async ({ userId, campaignId, contractId, data }) => {
    return VonqCampaignService.savePostingRequirementsData({ userId, campaignId, contractId, data });
};

API.duplicateCampaign = async ({ userId, request, campaignId}) => {
    return await VonqCampaignService.duplicateCampaign({ userId, request, campaignId});
};

API.getContractGroups = async ({ userId, request }) => {
    const results = await VonqGroupService.getContractGroups({ userId, request });
    return Mapper.mapList(results, [
        {
            a: 'id',
            b: 'id',
        },
        {
            a: 'name',
            b: 'name',
            mutations: {
                write: {
                    mutate: (value, destObj) => {
                        // regex to check if the group id is a valid UUID, only used to add contracts to the default group when there were created before the groups feature was added.
                        const regex = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
                        destObj['isUUID'] = regex.test(value);
                        return value;
                    },
                },
            },
        },
        {
            a: 'contracts',
            b: 'contracts'
        },
        {
            a: 'position',
            b: 'position'
        }
    ]);
};

API.editContractGroup = async ({ userId, groupId, groupName }) => {  
    const request = {
        name: groupName
    };
    return await VonqGroupService.editContractGroup({ userId, request, groupId });
};

API.moveContractGroups = async ({ userId, request }) => {  
    return await VonqGroupService.moveContractGroups({ userId, request });
};

API.deleteContractGroup = async ({ userId, groupId }) => {
    return await VonqGroupService.deleteContractGroup({userId, groupId});
}
